import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { filter, map } from 'lodash';
import { ListEditor, ImageEditor, TextEditor, CodeEditor, Prompt, RequestSync, Confirm } from '.';
import {
  CLIENT_STATE,
  UPDATE_SYNC_PAGE_DATA,
  REMOVE_SYNC_DATA,
  GENERAL_SETTINGS,
  CREATE_PUBLISH_DATA,
  PAGE_DATA,
  PAGE_LIST,
} from '../../../graphqls';
import getUserId from '../../../util/getUserId';

export default function Popup({ startPolling, stopPolling }) {
  const [withPublish, setWithPublish] = useState(false);
  const { loading: loadingClientState, data: clientState = {} } = useQuery(CLIENT_STATE);
  const { loading: loadingGeneralSettings, data: { generalSettings: { publishedAt } = {} } = {} } = useQuery(GENERAL_SETTINGS);
  const [createPublishData] = useMutation(CREATE_PUBLISH_DATA, {
    onCompleted: () => window.location.reload(),
  });
  const [updateSyncPageData] = useMutation(UPDATE_SYNC_PAGE_DATA, {
    refetchQueries: [{ query: GENERAL_SETTINGS }, { query: PAGE_DATA }, { query: PAGE_LIST }],
    onCompleted: () => {
      if (withPublish) {
        const userId = getUserId(window.userId);
        if (!userId) {
          return console.error('userId should exist');
        }
        createPublishData({ variables: { userId } });
      } else {
        window.location.reload();
      }
    },
  });
  const [removeSyncData] = useMutation(REMOVE_SYNC_DATA, {
    onCompleted: () => window.location.reload(),
  });
  const onTogglePublish = () => setWithPublish(!withPublish);
  const { modalTypeShown, checked } = clientState;

  if (loadingClientState || !modalTypeShown || loadingGeneralSettings) {
    return '';
  }

  const onSave = () =>
    updateSyncPageData({
      variables: {
        affectedResults: JSON.stringify(
          map(
            filter(checked, ({ sync }) => sync.length),
            ({ id, sync }) => ({ [id]: sync }),
          ),
        ),
      },
    });
  const onClear = () => removeSyncData();

  switch (modalTypeShown) {
    case 'logo':
      return <ImageEditor width={640} height={480} />;
    case 'commonBanner':
      return <ImageEditor width={1600} height={100} />;
    case 'bannerImage':
      return <ImageEditor width={1600} height={450} />;
    case 'bannerText':
      return <TextEditor />;
    case 'newsFeed':
      return <ListEditor />;
    case 'IntroText':
      return <TextEditor />;
    case 'featureImage':
      return <ImageEditor width={800} height={400} />;
    case 'featureText':
      return <TextEditor />;
    case 'subText':
      return <TextEditor />;
    case 'introText':
      return <TextEditor />;
    case 'cssEditor':
      return <CodeEditor />;
    case 'footerText':
      return <TextEditor />;
    case 'title':
      return <Prompt type="title" />;
    case 'description':
      return <TextEditor />;
    case 'latestUpdate':
      return <TextEditor />;
    case 'additionalText':
      return <TextEditor />;
    case 'additionalBottomText':
      return <TextEditor />;
    case 'additionalMiddleText':
      return <TextEditor />;
    case 'additionalUpperMiddleText':
      return <TextEditor />;
    case 'requestSync': {
      return <RequestSync startPolling={startPolling} stopPolling={stopPolling} />;
    }
    case 'saveSync': {
      return (
        <Confirm
          headerTitle="Overwrite Data"
          popupMessage={
            withPublish
              ? `This action will overwrite your current data with all selected data ${
                  publishedAt ? 'without republishing' : 'in a Published state'
                }.`
              : 'This action will overwrite your current data with all selected data in an unpublished state. Following this action, you can continue to edit your content in the editor.'
          }
          confirmedMessage="Applying synced data. Window will automatically close once process completes."
          buttonLabel="Overwrite"
          publishedAt={publishedAt}
          withPublish={withPublish}
          canPublish
          onTogglePublish={onTogglePublish}
          onConfirm={onSave}
        />
      );
    }
    case 'discardSync': {
      return (
        <Confirm
          headerTitle="Exit and Delete Sync"
          popupMessage="This action will delete your last synced data without making any changes to your existing web content."
          confirmedMessage="Sync is Deleted."
          buttonLabel="Delete"
          isDelete
          onConfirm={onClear}
        />
      );
    }
    case 'subPlanContentLabels':
      return <Prompt type="subPlanContentLabels" />;
    case 'measureDetail':
      return <TextEditor />;
    case 'comment':
      return <TextEditor />;
    case 'loading':
      return <h1>Loading...</h1>;
    case 'visualText':
      return <TextEditor />;
    default:
      return '';
  }
}
